import { TitleCasePipe } from '@angular/common';
import { Component, computed, DestroyRef, effect, inject, input, linkedSignal, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { provideIcons } from '@ng-icons/core';
import { lucideCircleAlert } from '@ng-icons/lucide';
import { HlmIconImports } from '@spartan-ng/helm/icon';
import { HlmTabsImports } from '@spartan-ng/helm/tabs';
import { HlmTooltipImports } from '@spartan-ng/helm/tooltip';
import { debounceTime, distinctUntilChanged } from 'rxjs';

import { Cartographie } from '../../../cartographie/models/cartographie.model';
import { ÉTAT_VALIDATION_ÉTAPE } from '../../models/état-validation-étape.enum';
import { EtapeStore } from '../../parcours-etape/store/etape.store';
import { DetailContratComponent } from './detail-contrat/detail-contrat.component';
import { DetailFormationsReglementairesComponent } from './detail-formations-reglementaires/detail-formations-reglementaires.component';
import { DetailGeneralComponent } from './detail-general/detail-general.component';
import { DetailLivrablesComponent } from './detail-livrables/detail-livrables.component';
import { DetailObjectifsComponent } from './detail-objectifs/detail-objectifs.component';
import { EtapeDetailFormService } from './étape-detail-form.service';
import { Référentiel } from '../../../administration/gestion-référentiels/models/référentiel.model';

const KEY_TAB_ETAPE = 'etape.tab';

@Component({
  selector: 'app-etape-detail-contenu',
  imports: [
    DetailGeneralComponent,
    DetailContratComponent,
    DetailObjectifsComponent,
    DetailFormationsReglementairesComponent,
    ReactiveFormsModule,
    HlmTabsImports,
    TitleCasePipe,
    DetailLivrablesComponent,
    HlmIconImports,
    HlmTooltipImports,
  ],
  templateUrl: './etape-detail-contenu.component.html',
  providers: [provideIcons({ lucideCircleAlert })],
})
export class EtapeDetailContenuComponent implements OnInit {
  readonly detailFormService = inject(EtapeDetailFormService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #etapeStore = inject(EtapeStore);

  readonly cartographie = input.required<Cartographie>();
  readonly referentiel = input.required<Référentiel>();
  readonly inputPeutModifier = input.required<boolean>({ alias: 'peutModifier' });
  readonly showErrors = input<boolean>();

  readonly etape = computed(() => this.#etapeStore.selectedEntity());
  readonly peutModifierContrat = computed(() => this.peutModifierGeneral() && !this.etape()?.estDupliquee);
  readonly peutModifierGeneral = computed(
    () =>
      this.inputPeutModifier() &&
      this.etape()?.estOuverte! &&
      this.etape()?.etatValidation === ÉTAT_VALIDATION_ÉTAPE.EN_COURS &&
      !this.etape()?.estSupprimee &&
      !this.etape()?.estExportee,
  );
  readonly traductions = linkedSignal(() => this.referentiel().metadonnees.traductions);
  readonly activeTab = signal(sessionStorage.getItem(KEY_TAB_ETAPE) ?? 'general');

  readonly #form = this.detailFormService.form;

  constructor() {
    effect(() => this.detailFormService.showErrors.set(this.showErrors()));
  }

  ngOnInit(): void {
    this.#form.valueChanges
      .pipe(takeUntilDestroyed(this.#destroyRef), debounceTime(1000), distinctUntilChanged())
      .subscribe(() => this.detailFormService.submitÉtape());
  }

  tabActivated(tab: string) {
    sessionStorage.setItem(KEY_TAB_ETAPE, tab);
    this.activeTab.set(tab);
  }

  isRepartitionAnneesCorrect() {
    return (this.detailFormService.repartitionsAnnee.value ?? []).filter((x) => x.nombreMois === 0).length > 0;
  }
}
